import { Box, Stack } from '@mui/material';
import {
  useGetGuestJourneyOfferImpactCardsQuery,
  useGetGuestJourneyOfferImpactSankeyQuery,
} from 'api/reports-service/guest-journey-hooks';
import SearchToolbar from 'components/search/search-toolbar/search-toolbar.component';
import {
  MultipleSearchQueryProvider,
  SearchParamsProvider,
} from 'components/search/search.context';
import {
  useSearchParam,
  useSearchParams,
  useSearchQuery,
} from 'components/search/search.hooks';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OfferImpactBootstrapData,
  offerImpactFilteredSearchParams,
  useOfferImpactSearchParams,
} from '../guest-journey.hooks';
import MovementSankey from '../movement/movement-sankey.component';
import { buildMovementSankey } from '../movement/movement-sankey.utils';
import ReportFieldsLink from '../report-fields/report-fields-link.component';
import MovementSummaryCards from '../movement/movement-summary-cards.component';
import MovementTable from '../movement/movement-table.component';
import OfferImpactBootstrap from './offer-impact-bootstrap.component';

/**
 * Queries only send the params on this tab's filter bar (Locations + Date
 * Range + Offer).
 */
const filteredSearchParams = offerImpactFilteredSearchParams;

/**
 * The twelve Offer Impact headline metrics, in the order they appear in the
 * design's rendered KPI row (`kpi.offer` + `offerTotals`). Titles, values and
 * any "vs previous period" trend ride on the BE cards.
 */
const cardOrder = [
  'avgCheck30d',
  'avgVisits30d',
  'avgCheck90d',
  'avgVisits90d',
  'membersChangedStage',
  'avgCheckOnRedemption',
  'totalDistributions',
  'totalRedemptions',
  'redemptionRate',
  'totalDiscount',
  'totalSpendNetSales',
  'discountRatio',
];

/**
 * Post-redemption cards need the selected date range to span both how old the
 * redemption is *and* the N-day after-window: the 30-day cards need ≥ 60 days
 * selected, the 90-day cards need the full ≥ 365 days (sc-371890). Shorter ranges
 * can't compute them, so they show the shared "Limited data" indicator — the same
 * one Retention uses — instead of a value.
 */
const POST_REDEMPTION_HORIZONS: { id: string; minDays: number }[] = [
  { id: 'avgCheck30d', minDays: 60 },
  { id: 'avgVisits30d', minDays: 60 },
  { id: 'avgCheck90d', minDays: 365 },
  { id: 'avgVisits90d', minDays: 365 },
];

/**
 * Gate the tab behind the bootstrap fetch (the GJ offer list). Search params are
 * only built once the bootstrap resolves, so the Offer filter gets a valid
 * synchronous default (a real offer id) — hence the provider lives below the
 * bootstrap, not at the top.
 */
const SearchParamsWrapper = () => (
  <OfferImpactBootstrap>
    {data => <OfferImpactReady data={data} />}
  </OfferImpactBootstrap>
);

/**
 * Mounted only after the bootstrap has the offer list, so the search-param hook
 * (and the queries it drives) always start from a valid offer default.
 */
const OfferImpactReady = ({ data }: { data: OfferImpactBootstrapData }) => {
  const searchParams = useSearchParams(useOfferImpactSearchParams(data));
  return (
    <SearchParamsProvider value={searchParams}>
      <OfferImpact />
    </SearchParamsProvider>
  );
};

const OfferImpact = () => {
  const { t } = useTranslation();

  const summary = useSearchQuery(useGetGuestJourneyOfferImpactCardsQuery, {
    filteredSearchParams,
  });
  // One movement payload — the sankey and both tables render from it.
  const movement = useSearchQuery(useGetGuestJourneyOfferImpactSankeyQuery, {
    filteredSearchParams,
  });

  const offerParam = useSearchParam<string>('offer');
  const selectedOffer = offerParam?.options?.find(
    o => o.id === offerParam?.value,
  );
  const centerLabel = selectedOffer?.label;

  // The post-redemption cards can only be computed when the selected range is
  // long enough for their window; shorter ranges show the "Limited data"
  // indicator (see POST_REDEMPTION_HORIZONS). The BE doesn't flag this — the FE
  // compares the range against each metric's horizon, same as Retention.
  const rangeStart = useSearchParam<DateTime>('startdate')?.value;
  const rangeEnd = useSearchParam<DateTime>('enddate')?.value;
  const rangeDays =
    rangeStart && rangeEnd
      ? Math.max(0, Math.round(rangeEnd.diff(rangeStart, 'days').days))
      : undefined;
  const limitedCardIds =
    rangeDays == null
      ? []
      : POST_REDEMPTION_HORIZONS.filter(c => rangeDays < c.minDays).map(
          c => c.id,
        );

  // Stage names arrive display-ready from the BE (configured per client), so the
  // sankey and tables render them verbatim — no FE label resolution.
  const stayedLabel = t('page.guestJourney.movement.stayed');
  const sankeyGraph = useMemo(
    () =>
      movement.data &&
      buildMovementSankey(movement.data, {
        centerId: offerParam?.value ?? 'center',
        stayedLabel,
        // Outgoing flows arrive per destination stage; the offer sankey shows
        // them aggregated into directional movement buckets (story 357748).
        aggregateOutgoingByMovement: true,
      }),
    [movement.data, offerParam?.value, stayedLabel],
  );

  return (
    <Stack marginTop={2} data-testid="GuestJourney.OfferImpact">
      <MultipleSearchQueryProvider
        value={{
          queries: { summary, movement },
        }}
      >
        <Stack spacing={2}>
          <SearchToolbar />

          <ReportFieldsLink tab="offerImpact" />

          <MovementSummaryCards
            testIdPrefix="OfferImpact"
            cardOrder={cardOrder}
            data={summary.data}
            loading={summary.queryState.isFetching}
            limitedCardIds={limitedCardIds}
            gridTemplateColumns={{
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
              xl: 'repeat(6, 1fr)',
            }}
          />

          <MovementSankey
            data={sankeyGraph}
            loading={movement.queryState.isFetching}
            centerLabel={centerLabel}
            // Incoming offer flows are all neutral redemptions; per the design
            // they read as blue — light-blue edge, deeper-blue node bar — rather
            // than the default movement grey.
            incomingNeutralColors={{
              edge: 'rgb(179, 229, 252)',
              node: 'rgb(41, 182, 246)',
            }}
            data-testid="OfferImpact.Sankey"
          />

          <Box
            display="grid"
            gap={2}
            gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }}
          >
            <MovementTable
              title={t('page.guestJourney.movement.incoming')}
              rows={movement.data?.incoming ?? []}
              loading={movement.queryState.isFetching}
              hideMovementColumn
              data-testid="OfferImpact.Incoming"
            />
            <MovementTable
              title={t('page.guestJourney.movement.outgoing')}
              rows={movement.data?.outgoing ?? []}
              loading={movement.queryState.isFetching}
              movementColumnFirst
              data-testid="OfferImpact.Outgoing"
            />
          </Box>
        </Stack>
      </MultipleSearchQueryProvider>
    </Stack>
  );
};

export default SearchParamsWrapper;
